import { useState } from 'react';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { AlertTriangle, Play, LogOut } from 'lucide-react';
import { useNavigate } from "react-router-dom";
import { authFetch } from '../lib/authFetch';
import { API_BASE_URL } from '../lib/authFetch';

interface PausedAccountModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onReactivate?: () => Promise<void>;
  reactivating?: boolean;
}

export default function PausedAccountModal({ open, onOpenChange, onReactivate, reactivating = false }: PausedAccountModalProps) {
  const [isReactivating, setIsReactivating] = useState(false);
  const navigate = useNavigate();

  const handleReactivate = async () => {
    if (onReactivate) {
      // Se foi passada uma função onReactivate, usa ela
      await onReactivate();
    } else {
      // Lógica original para quando usado no dashboard
      setIsReactivating(true);
      try {
        const response = await fetch(`${API_BASE_URL}/subscriptions/me/resume`, {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${localStorage.getItem('token')}`
          }
        });

        if (response.ok) {
          alert('Assinatura reativada com sucesso! Você será redirecionado para o dashboard.');
          onOpenChange(false);
          window.location.reload(); // Recarrega para atualizar o estado
        } else {
          const errorData = await response.json();
          alert(errorData.detail || 'Erro ao reativar assinatura');
        }
      } catch {
        alert('Erro de conexão com o servidor');
      } finally {
        setIsReactivating(false);
      }
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('userName');
    localStorage.removeItem('refresh_token');
    navigate('/');
  };

  if (!open) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-protec-dark-light border-yellow-500/30 max-w-md">
        <DialogHeader>
          <DialogTitle className="text-white flex items-center gap-2">
            <AlertTriangle className="w-5 h-5 text-yellow-400" />
            Conta Pausada
          </DialogTitle>
          <DialogDescription className="text-gray-300">
            Sua assinatura está temporariamente suspensa
          </DialogDescription>
        </DialogHeader>
        
        <div className="space-y-4">
          <div className="bg-yellow-500/10 border border-yellow-500/20 rounded-lg p-4">
            <h4 className="text-yellow-400 font-medium mb-2">ℹ️ Status da Conta</h4>
            <ul className="text-gray-300 text-sm space-y-1">
              <li>• Sua assinatura foi pausada</li>
              <li>• Não há cobranças durante a pausa</li>
              <li>• Seus dados estão preservados</li>
              <li>• Acesso às funcionalidades está limitado</li>
            </ul>
          </div>
          
          <div className="flex gap-3 justify-center">
            <Button 
              onClick={handleReactivate}
              disabled={reactivating || isReactivating}
              className="bg-green-500 text-white hover:bg-green-600 disabled:opacity-50"
            >
              <Play className="w-4 h-4 mr-2" />
              {reactivating || isReactivating ? 'Reativando...' : 'Reativar Conta'}
            </Button>
            <Button 
              variant="outline"
              onClick={handleLogout}
              disabled={reactivating || isReactivating}
              className="border-gray-500 text-gray-300 hover:bg-gray-500 hover:text-white disabled:opacity-50"
            >
              <LogOut className="w-4 h-4 mr-2" />
              Sair
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
} 